import { LitElement, html, nothing } from 'lit';
import { map } from 'lit/directives/map.js';
import { ref, createRef } from 'lit/directives/ref.js';
import { keyed } from 'lit/directives/keyed.js';
import { guard } from 'lit/directives/guard.js';
import { live } from 'lit/directives/live.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { animate } from '@lit-labs/motion';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import money from '../utilities/formatMoney';
import SplideController from '../controllers/splide_controller';
import HoverController from '../controllers/hover_controller';

class ProductCard extends LitElement {
  static properties = {
    product: { type: Object, attribute: false },
    siblings: { type: Array, attribute: false },

    selected: {
      type: Boolean,
      attribute: 'selected',
      reflect: true,
    },
    wishlistId: { type: Number, attribute: 'wishlist-id' },
    swatchesOpen: { type: Boolean, state: true },
    siblingSelected: { type: Boolean, state: true },
    tempProduct: { type: Object, state: true },
    displayProduct: { type: Object, state: true },
    variantsOpen: { type: Boolean, state: true },
    addingToCart: { type: Boolean, state: true },
    selectedVariant: { type: Object, state: true },
    wishlistFeatures: {
      type: Boolean,
      attribute: 'wishlist-features',
      reflect: true,
    },

    loading: { type: String, attribute: true },
    discount: {
      type: Object,
      attribute: 'discount',
      converter: {
        fromAttribute: (value) => (value ? JSON.parse(value) : null),
        toAttribute: (value) => (value ? JSON.stringify(value) : null),
      },
    },
    hideSwatches: {
      type: Boolean,
      attribute: 'hide-swatches',
      reflect: true,
    },
    hideHoverImage: {
      type: Boolean,
      attribute: 'hide-hover-image',
      reflect: true,
    },
    hideDiscountAmount: {
      type: Boolean,
      attribute: 'hide-discount-amount',
      reflect: true,
    },
    hideDiscountSitewide: {
      type: Boolean,
      attribute: 'hide-discount-sitewide',
      reflect: true,
    },
    hideWishlist: {
      type: Boolean,
      attribute: 'hide-wishlist',
      reflect: true,
    },
    hideBadges: {
      type: Boolean,
      attribute: 'hide-badges',
      reflect: true,
    },
    mode: {
      type: String,
      attribute: true,
      reflect: true,
      hasChanged(newVal, oldVal) {
        return newVal !== oldVal;
      },
    },
    selectedVariantId: {
      type: String,
      attribute: 'selected-variant-id',
      reflect: true,
      hasChanged(newVal, oldVal) {
        return String(newVal) !== String(oldVal);
      },
    },
    selectedProductId: {
      type: String,
      attribute: 'selected-product-id',
      reflect: true,
      hasChanged(newVal, oldVal) {
        return String(newVal) !== String(oldVal);
      },
    },
    initialSelectedVariant: { type: Object, attribute: false },
  };

  createRenderRoot() {
    return this;
  }

  variantSelectRef = createRef();
  wishlistButtonRef = createRef();
  productSliderRef = createRef();

  constructor() {
    super();

    this.splideController = new SplideController(this, {
      type: 'slide',
      speed: 300,
      rewind: true,
      drag: true,
      arrows: true,
      pagination: false,
      updateOnMove: false,
      flickMaxPages: 1,
      lazyLoad: 'sequential',
      preloadPages: 0,
      gap: 0,
      perPage: 1,
      perMove: 1,
      direction: 'ltr',
      height: 'auto',
      waitForTransition: false,
      reducedMotion: {
        speed: 0,
        rewindSpeed: 0,
      },
    });
    this.hoverController = new HoverController(this, 30);
    this.isHovering = false;

    if (!window.productCardVariantSelections) {
      window.productCardVariantSelections = new Map();
    }
  }

  firstUpdated() {
    this._setupSwatchEventDelegation();

    this.addEventListener('wishlist:variant-updated', (e) => {
      const currentProductId = this.displayProduct?.id || this.product.id;
      if (e.detail.productId === String(currentProductId)) {
        this.selectedProductId = e.detail.productId;
        this.selectedVariantId = e.detail.variantId;
        this._syncSelectElement();
      }
    });
  }

  _setupSwatchEventDelegation() {
    this.addEventListener(
      'mouseenter',
      this._handleSwatchHover.bind(this),
      true
    );
    this.addEventListener('focus', this._handleSwatchHover.bind(this), true);
    this.addEventListener('click', this._handleSwatchClick.bind(this), true);
  }

  _handleSwatchHover(e) {
    const swatchLabel = e.target.closest('.product-card__swatch');
    if (!swatchLabel) return;

    const siblingId = swatchLabel.getAttribute('data-sibling-id');
    if (siblingId) {
      this.hoverController.debounceHover(siblingId);
    }
  }

  _handleSwatchClick(e) {
    const swatchInput = e.target.closest('.product-card__swatch-input');
    if (!swatchInput) return;

    this._handleProduct(e);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.hoverOutTimeout) {
      clearTimeout(this.hoverOutTimeout);
      this.hoverOutTimeout = null;
    }
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      'wishlist:add',
      this._handleSwatchUpdate.bind(this)
    );
  }

  _updateSplide() {
    this.splideController.refresh();
  }

  async updated(changedProperties) {
    super.updated?.(changedProperties);

    if (
      changedProperties.has('product') ||
      changedProperties.has('selectedVariantId') ||
      changedProperties.has('selectedProductId') ||
      changedProperties.has('initialSelectedVariant')
    ) {
      if (!this.displayProduct) {
        this.displayProduct = this.product;
      }

      this.selectedProductId = String(
        this.displayProduct?.id || this.product?.id
      );

      const wishlistButton = this.wishlistButtonRef.value;
      if (wishlistButton) {
        const currentProduct = this.displayProduct || this.product;
        wishlistButton.setAttribute('product-handle', currentProduct.handle);
        wishlistButton.requestUpdate?.();
      }

      // Check if user has made a selection that's stored
      const currentProduct = this.displayProduct || this.product;
      const storedVariantId = await this._getStoredVariantSelection(
        currentProduct.id
      );

      if (
        changedProperties.has('initialSelectedVariant') &&
        this.initialSelectedVariant &&
        !storedVariantId
      ) {
        // Only use initial variant if no stored selection exists
        this.selectedVariant = this.initialSelectedVariant;
        this.selectedVariantId = String(this.initialSelectedVariant.id);
      } else {
        this._resolveSelectedVariant();
      }

      if (changedProperties.has('product')) {
        this._updateSplide();
      }

      this.dispatchEvent(
        new CustomEvent('product-card:updated', {
          bubbles: true,
          composed: true,
        })
      );
    }

    if (
      changedProperties.has('selectedVariant') ||
      changedProperties.has('selectedVariantId')
    ) {
      this._syncSelectElement();
    }

    if (
      changedProperties.has('tempProduct') ||
      changedProperties.has('displayProduct')
    ) {
      const selectedEl = this.variantSelectRef.value;
      if (selectedEl) {
        if (this.selectedVariant) {
          selectedEl.value = this.selectedVariant.id;
        } else {
          selectedEl.selectedIndex = 0;
        }
      }

      this._updateSplide();
    }
  }

  _handleSwatchUpdate(e) {
    if (
      this.displayProduct &&
      e.detail?.productId === String(this.displayProduct?.id)
    ) {
      setTimeout(() => {
        this.tempProduct = undefined;
        this.displayProduct = this.product;

        this.selectedProductId = String(this.product?.id);

        this.siblingSelected = false;

        this.requestUpdate();
      }, 550);
    }
  }

  async _addVariant(e, variant) {
    e.preventDefault();
    e.stopPropagation();
    try {
      this.addingToCart = true;
      await window.cartUtils.addItem(variant.id, 1);
      window.drawerFunctions.toggleDrawer('drawer_sidecart', true);
    } catch (error) {
      console.error(error);
    } finally {
      this.addingToCart = false;
    }
  }

  _handleHoverIn(currentId) {
    this.isHovering = true;
    clearTimeout(this.hoverOutTimeout);

    const inputElement = this.querySelector('input[checked]');
    if (inputElement) {
      inputElement.checked = false;
      inputElement.removeAttribute('checked');
    }

    const tempProd = this.siblings?.find(
      (sibling) => sibling.id === Number(currentId)
    );
    this.tempProduct = tempProd;
  }

  _handleHoverOut() {
    this.isHovering = false;
    this.hoverController.clearTimeout();

    this.hoverOutTimeout = setTimeout(() => {
      if (!this.isHovering) {
        this.tempProduct = undefined;

        const activeProductId = this.displayProduct?.id || this.product.id;
        const oldChecked = this.querySelector(
          `input[data-id="${activeProductId}"]`
        );
        if (oldChecked) {
          oldChecked.checked = true;
          oldChecked.setAttribute('checked', '');
        }
      }
    }, 75);
  }

  async _getStoredVariantSelection(productId) {
    if (this.wishlistFeatures && window.wishlist) {
      const wishlist = await window.wishlist.retrieve();
      const storedVariantId =
        wishlist.productData[productId]?.selectedVariantId;
      if (storedVariantId) {
        return storedVariantId;
      }
    }

    const sessionValue = window.productCardVariantSelections.get(productId);
    return sessionValue;
  }

  async _setStoredVariantSelection(productId, variantId) {
    if (this.wishlistFeatures && window.wishlist) {
      const wishlist = await window.wishlist.retrieve();
      if (wishlist.productData?.[productId]) {
        return;
      }
    }

    window.productCardVariantSelections.set(productId, variantId);
  }

  _handleProduct(e) {
    clearTimeout(this.hoverOutTimeout);
    this.siblingImages = this.querySelectorAll('img[data-sibling]');
    if (!this.siblingImages || this.siblingImages.length < 0) {
      return;
    }

    const target = e.target;
    const currentId = target?.dataset.id;
    target.setAttribute('checked', '');

    this.displayProduct =
      this.siblings?.find((sibling) => sibling.id === Number(currentId)) ||
      this.displayProduct;

    this.selectedProductId = String(this.displayProduct?.id);

    this.tempProduct = undefined;

    const variantSelect = this.variantSelectRef.value;

    if (variantSelect && this.selectedVariant) {
      variantSelect.value = this.selectedVariant.id;
    } else if (variantSelect) {
      variantSelect.selectedIndex = 0;
    }

    this.product.id === Number(currentId)
      ? (this.siblingSelected = false)
      : (this.siblingSelected = true);

    this.dispatchEvent(
      new CustomEvent('product-card:swatch-changed', {
        detail: {
          selected: this.selected,
          originalId: this.product.id,
          newSiblingId: this.displayProduct.id,
          cardId: this.wishlistId
            ? `card-${this.wishlistId}-${this.displayProduct.id}`
            : `card-${this.product.id}`,
        },
        bubbles: true,
        composed: false,
      })
    );
  }

  async _handleSwatches() {
    const wasOpen = this.swatchesOpen;
    this.swatchesOpen = !this.swatchesOpen;

    await this.updateComplete;
    if (!wasOpen && this.swatchesOpen) {
      const revealedSwatches = this.querySelectorAll(
        '.product-card__swatch-input'
      );
      console.log(revealedSwatches);
      if (revealedSwatches && revealedSwatches.length > 5) {
        revealedSwatches[4].focus();
      }
    }
  }

  async _resolveSelectedVariant() {
    const product = this.displayProduct || this.product;
    if (!product || !product.variants || product.variants.length === 0) {
      this.selectedVariant = undefined;
      return;
    }

    if (this.selectedVariantId) {
      const match = product.variants.find(
        (v) => String(v.id) === String(this.selectedVariantId)
      );
      if (match) {
        this.selectedVariant = match;
        return;
      }
      this.selectedVariant = undefined;
      this.selectedVariantId = undefined;
      return;
    }

    const storedVariantId = await this._getStoredVariantSelection(product.id);
    if (storedVariantId) {
      const match = product.variants.find(
        (v) => String(v.id) === String(storedVariantId)
      );
      if (match) {
        this.selectedVariant = match;
        this.selectedVariantId = String(match.id);
        return;
      }
    }

    this.selectedVariant = undefined;

    if (product.variants.length === 1) {
      this.selectedVariant = product.variants[0];
      this.selectedVariantId = String(product.variants[0].id);

      if (this.wishlistFeatures) {
        const wishlist = await window.wishlist.retrieve();

        if (wishlist.productData[product.id]?.selectedVariantId) {
          return;
        }

        window.wishlist.updateVariant(product.id, product.variants[0].id);
      }
    }
  }

  _renderSwatch(sibling) {
    const productId = this.product.id;
    const currentProduct =
      this.tempProduct || this.displayProduct || this.product;
    const isChecked = sibling.title === currentProduct.title;
    const onlineStoreUrl = sibling.online_store_url;

    const guardDeps = [
      sibling.id,
      sibling.title,
      sibling.tags,
      currentProduct.title,
      productId,
    ];

    return keyed(
      sibling.id,
      guard(guardDeps, () => {
        let tone = 'white';
        let colour = 'white';

        for (const tag of sibling.tags) {
          if (tag.includes('tone:')) {
            tone = tag.split('tone:').pop()?.toLowerCase() ?? 'white';
          }
          if (tag.includes('colour:')) {
            colour = tag.split('colour:').pop()?.toLowerCase() ?? 'white';
          }
        }

        const backgroundName =
          colour !== 'white' ? colour : tone.replace('/', '_');
        const backgroundNameJoined = backgroundName.split(' ').join('-');

        const col = colour.replace(' ', '_').replace('/', '-');
        const t = tone.replace(' ', '_').replace('/', '-');

        const className = `${
          colour != 'white' ? `swatch_colour_${col}` : ''
        } swatch_tone_${t}`;

        return html`
          <label
            tabindex="-1"
            data-sibling-id="${sibling.id}"
            for="${productId}_swatch_${backgroundNameJoined}_${sibling.id}"
            class="${className} product-card__swatch"
          >
            <span class="sr-only">${backgroundName} colour swatch</span>
            <input
              type="radio"
              name="${productId}_swatches"
              class="product-card__swatch-input"
              value="${backgroundNameJoined}"
              id="${productId}_swatch_${backgroundNameJoined}_${sibling.id}"
              aria-label="${backgroundName} colour swatch"
              .checked=${live(isChecked)}
              data-id=${sibling.id}
            />
          </label>
        `;
      })
    );
  }

  _renderMedia() {
    const displayProduct =
      this.tempProduct || this.displayProduct || this.product;

    const desktopView =
      this.siblings !== null && this.swatches && this.siblingSelected
        ? map(this.siblings, (sibling) =>
            sibling.id === this.product.id
              ? html`
                  <picture
                    class="product-card__media-picture"
                    ${animate({
                      in: [{ opacity: 0 }, { opacity: 1 }],
                      out: [{ opacity: 1, opacity: 0 }],
                      duration: 500,
                      easing: 'ease-in-out',
                    })}
                  >
                    <img
                      data-sibling=${sibling.id}
                      class="product-card__media-image ${!this.hideHoverImage
                        ? 'product-card__media-image--primary'
                        : ''}"
                      src=${(displayProduct && displayProduct.media[0].src) ||
                      sibling.media[0].src}
                      alt=${(displayProduct && displayProduct.title) ||
                      sibling.title}
                      loading="${this.loading || 'lazy'}"
                      fetchpriority="${this.loading === 'eager'
                        ? 'high'
                        : 'auto'}"
                      sizes="calc((100vw / 4) - 16px), (min-width: 768px) calc((100vw / 3) - 16px)"
                      srcset=${this._getSrcset(
                        (displayProduct && displayProduct.media[0].src) ||
                          sibling.media[0].src,
                        (displayProduct && displayProduct.media[0].width) ||
                          sibling.media[0].width,
                        (displayProduct && displayProduct.media[0].height) ||
                          sibling.media[0].height
                      )}
                      width="250"
                      height="400"
                    />
                  </picture>
                  ${!this.hideHoverImage && displayProduct.media[1]
                    ? html`<picture
                        class="product-card__media-picture"
                        ${animate({
                          in: [{ opacity: 0 }, { opacity: 1 }],
                          out: [{ opacity: 1, opacity: 0 }],
                          duration: 500,
                          easing: 'ease-in-out',
                        })}
                      >
                        <img
                          data-sibling=${sibling.id}
                          class="product-card__media-image ${!this
                            .hideHoverImage
                            ? 'product-card__media-image--secondary'
                            : ''}"
                          src=${(displayProduct &&
                            displayProduct.media[1].src) ||
                          sibling.media[1].src}
                          alt=${(displayProduct && displayProduct.title) ||
                          sibling.title}
                          loading="${this.loading || 'lazy'}"
                          fetchpriority="${this.loading === 'eager'
                            ? 'high'
                            : 'auto'}"
                          sizes="calc((100vw / 4) - 16px), (min-width: 768px) calc((100vw / 3) - 16px)"
                          srcset=${this._getSrcset(
                            (displayProduct && displayProduct.media[1].src) ||
                              sibling.media[1].src,
                            (displayProduct && displayProduct.media[1].width) ||
                              sibling.media[1].width,
                            (displayProduct &&
                              displayProduct.media[1].height) ||
                              sibling.media[1].height
                          )}
                          width="250"
                          height="400"
                        />
                      </picture>`
                    : nothing}
                `
              : nothing
          )
        : html`
            ${displayProduct.media[0] && displayProduct.media[0].src
              ? html`<picture
                  class="product-card__media-picture"
                  ${animate({
                    in: [{ opacity: 0 }, { opacity: 1 }],
                    out: [{ opacity: 1, opacity: 0 }],
                    duration: 500,
                    easing: 'ease-in-out',
                  })}
                >
                  <img
                    class="product-card__media-image ${!this.hideHoverImage
                      ? 'product-card__media-image--primary'
                      : ''}"
                    src=${this._getOptimizedImageUrl(
                      displayProduct.media[0].src,
                      250,
                      400
                    )}
                    srcset=${this._getSrcset(
                      displayProduct.media[0].src,
                      displayProduct.media[0].width,
                      displayProduct.media[0].height
                    )}
                    alt=${displayProduct.title}
                    loading="${this.loading || 'lazy'}"
                    fetchpriority="${this.loading === 'eager'
                      ? 'high'
                      : 'auto'}"
                    sizes="calc((100vw / 4) - 16px), (min-width: 768px) calc((100vw / 3) - 16px)"
                    width="250"
                    height="400"
                  />
                </picture>`
              : nothing}
            ${displayProduct.media[1] &&
            displayProduct.media[1].src &&
            !this.hideHoverImage
              ? html`<picture class="product-card__media-picture">
                  <img
                    class="product-card__media-image ${!this.hideHoverImage
                      ? 'product-card__media-image--secondary'
                      : ''}"
                    src=${this._getOptimizedImageUrl(
                      displayProduct.media[1].src,
                      250,
                      400
                    )}
                    alt=${displayProduct.title}
                    loading="${this.loading || 'lazy'}"
                    fetchpriority="${this.loading === 'eager'
                      ? 'high'
                      : 'auto'}"
                    sizes="calc((100vw / 4) - 16px), (min-width: 768px) calc((100vw / 3) - 16px)"
                    srcset=${this._getSrcset(
                      displayProduct.media[1].src,
                      displayProduct.media[1].width,
                      displayProduct.media[1].height
                    )}
                    width="250"
                    height="400"
                  />
                </picture>`
              : nothing}
          `;

    const mobileSlider = html`
      <div class="product-slider splide" ${ref(this.productSliderRef)}>
        ${displayProduct.media.length > 1
          ? html`
              <div class="splide__arrows">
                <button
                  class="splide__arrow splide__arrow--prev"
                  aria-label="Previous image"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="200"
                    height="200"
                    viewBox="0 0 120 120"
                    stroke="currentColor"
                    fill="transparent"
                    stroke-width="12"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  >
                    <path d="M36,15 l45,45 l-45,45"></path>
                  </svg>
                </button>
                <button
                  class="splide__arrow splide__arrow--next"
                  aria-label="Next image"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="200"
                    height="200"
                    viewBox="0 0 120 120"
                    stroke="currentColor"
                    fill="transparent"
                    stroke-width="12"
                    stroke-linejoin="round"
                    stroke-linecap="round"
                  >
                    <path d="M36,15 l45,45 l-45,45"></path>
                  </svg>
                </button>
              </div>
            `
          : nothing}
        <div class="splide__track">
          <ul class="splide__list">
            ${displayProduct.media.map(
              (media) =>
                html` ${media.media_type === 'video'
                  ? html`
                      <li class="product-slider-item splide__slide">
                        <a
                          href="/products/${displayProduct.handle}"
                          title="${displayProduct.title}"
                          class="product-card__slide-link"
                        >
                          <video
                            src=${media.sources && media.sources[0]
                              ? media.sources[0].url
                              : media.src}
                            autoplay
                            muted
                            loop
                            playsinline
                          >
                            <source
                              src=${media.sources && media.sources[0]
                                ? media.sources[0].url
                                : media.src}
                              type="video/mp4"
                            />
                          </video>
                        </a>
                      </li>
                    `
                  : html`
                      <li class="product-slider-item splide__slide">
                        <a
                          href="/products/${displayProduct.handle}"
                          title="${displayProduct.title}"
                          class="product-card__slide-link"
                        >
                          <picture>
                            <source
                              srcset=${this._getSrcset(
                                media.src,
                                media.width,
                                media.height
                              )}
                              type="image/webp"
                            />
                            <img
                              src=${this._getOptimizedImageUrl(
                                media.src,
                                250,
                                400
                              )}
                              alt=${displayProduct.title}
                              loading="${this.loading || 'lazy'}"
                              fetchpriority="${this.loading === 'eager'
                                ? 'high'
                                : 'auto'}"
                              width="125"
                              height="200"
                              sizes="(max-width: 767px) 50vw, (min-width: 768px) calc((100vw / 3) - 16px)"
                            />
                          </picture>
                        </a>
                      </li>
                    `}`
            )}
          </ul>
        </div>
      </div>
    `;

    return html`
      <div class="desktop-view">
        <a
          title="${displayProduct.title}"
          href="/products/${displayProduct.handle}"
          class="product-card__desktop-link"
        >
          ${desktopView}
        </a>
      </div>
      <div class="mobile-view">${mobileSlider}</div>
    `;
  }

  _getOptimizedImageUrl(src, width, height) {
    if (!src) return '';
    const parts = src.split('.');
    const extension = parts.pop();
    const base = parts.join('.');
    return `${base}_${width}x${height}.${extension}`;
  }

  _getSrcset(src, originalWidth, originalHeight) {
    if (!src || !originalWidth || !originalHeight) return '';
    const aspectRatio = originalHeight / originalWidth;
    const widths = [180, 360, 540, 720, 900, 1080, 1296, 1512, 1728, 2048];
    const srcsetParts = widths.map((width) => {
      const height = Math.round(width * aspectRatio);
      return `${this._getOptimizedImageUrl(src, width, height)} ${width}w`;
    });
    return srcsetParts.join(', ');
  }

  _showVariants() {
    this.variantsOpen = !this.variantsOpen;
  }

  async _handleVariantSelectChange(e, variant) {
    e.preventDefault();
    e.stopPropagation();

    const isInWishlist = this.wishlistButtonRef.value?.inWishlist;

    const currentProduct = this.displayProduct || this.product;
    if (currentProduct) {
      await this._setStoredVariantSelection(currentProduct.id, variant.id);
    }

    if (isInWishlist && ifDefined(this.wishlistFeatures === true)) {
      const currentProduct = this.displayProduct || this.product;
      const variantChangedEvent = new CustomEvent('wishlist:variant-changed', {
        detail: {
          variant: variant,
          product: currentProduct,
        },
        bubbles: true,
        composed: true,
      });

      this.dispatchEvent(variantChangedEvent);
    }

    if (variant) {
      this.selectedVariant = variant;
      this.selectedVariantId = String(variant.id);

      const currentProduct = this.displayProduct || this.product;
      await this._setStoredVariantSelection(currentProduct.id, variant.id);
    } else {
      this.selectedVariant = undefined;
    }
  }

  _syncSelectElement() {
    const selectEl = this.variantSelectRef.value;
    if (selectEl && this.selectedVariant) {
      selectEl.value = this.selectedVariant.id;
    }
  }

  _getCurrentVariant() {
    if (this.selectedVariant) {
      return this.selectedVariant;
    }

    const product = this.displayProduct || this.product;
    if (product?.variants.length === 1) {
      return product.variants[0];
    }

    return undefined;
  }

  _getButtonText(product, loggedIn) {
    const currentVariant = this._getCurrentVariant();

    if (!currentVariant) {
      return `${window.theme.strings.product_card.select_option.replace(
        '{{ option }}',
        product?.options?.[0] || 'option'
      )}`;
    } else if (currentVariant.available) {
      return window.theme.strings.addToCart;
    } else {
      if (!loggedIn && product.coming_soon_metafield) {
        return window.theme.strings.notifyMe;
      }
      return window.theme.strings.soldOut;
    }
  }

  _renderPrice(currentProduct) {
    const currentVariant = this._getCurrentVariant();
    const product = currentVariant || currentProduct;

    const discount = Math.round(
      ((product?.compare_at_price - product.price) /
        product?.compare_at_price) *
        100
    );

    return html`<span
      class="${product?.compare_at_price > product.price
        ? 'product-card__price-sale'
        : ''} product-card__price"
    >
      ${product?.compare_at_price > product.price
        ? html`<s class="product-card__price-compare"
            >${money(product?.compare_at_price)}</s
          >`
        : nothing}
      ${money(product.price)} ${window.Shopify.currency.active}
      ${product?.compare_at_price > product.price && !this.hideDiscountAmount
        ? html`${window.theme.strings.discount.discount_message.replace(
            '{{ discount }}',
            discount
          )}`
        : nothing}
    </span> `;
  }

  _handleCheckboxChange(e) {
    e.preventDefault();
    e.stopPropagation();
    this.selected = !this.selected;
    const currentProduct = this.displayProduct || this.product;
    this.dispatchEvent(
      new CustomEvent('product-card:selected', {
        detail: {
          selected: this.selected,
          productId: currentProduct.id,
          originalId: this.product.id,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  _renderCheckbox() {
    if (!this.product) return nothing;

    const id = this.product?.id;

    return html` <div class="product-card__checkbox">
      <label for="product-checkbox-${id}">
        <input
          type="checkbox"
          name="product-checkbox"
          id="product-checkbox-${id}"
          aria-label="Select product for bulk actions - ${this.product.title}"
          .checked="${live(this.selected)}"
          @change=${this._handleCheckboxChange}
        />
      </label>
    </div>`;
  }

  _renderDiscount() {
    const product = this.tempProduct || this.displayProduct || this.product;
    if (!product?.tags) return nothing;

    const promoMessage =
      window.theme?.promoMessageGenerator?.(product.tags, product.price) || '';

    if (!promoMessage) return nothing;

    return html`
      <span class="product-card__sitewide-sale">
        <span class="product-card__sitewide-sale-message">${unsafeHTML(promoMessage)}</span>
      </span>
    `;
  }

  _renderBadges() {
    const product = this.tempProduct || this.displayProduct || this.product;
    if (!product || !product.tags || !Array.isArray(product.tags)) {
      return nothing;
    }

    if (product.just_dropped_metafield) {
      return html`
        <div class="product-badge product-card__badge">
          ${ window.theme.strings.badgeJustDropped }
        </div>
      `;
    }

    if (product.coming_soon_metafield) {
      return html`
        <div class="product-badge product-card__badge">
          ${ window.theme.strings.badgeComingSoon }
        </div>
      `;
    }

    const badgeKey = window.theme.getProductBadgeKey?.(product.tags, {
      productId: product.id,
    });
    const badgeText = window.theme.getProductBadgeText?.(badgeKey);

    if (!badgeText) {
      return nothing;
    }

    return html`
      ${!this.hideBadges
        ? html`<div class="product-badge product-card__badge">
            ${badgeText}
          </div>`
        : nothing}
    `;
  }

  /**
   * Gets the display text for a badge from its tag
   * @param {string} badgeKey - The badge tag (e.g., "badge:new")
   * @returns {string|null} The display text for the badge or null
   */
  _getBadgeText(badgeKey) {
    const formattedBadge = badgeKey.replace('badge:', '').trim();

    if (
      window.badgeTextFromKey &&
      typeof window.badgeTextFromKey === 'function'
    ) {
      return window.badgeTextFromKey(formattedBadge);
    }

    const badgeMap = {
      new: 'New',
      sale: 'Sale',
      bestseller: 'Best Seller',
      limited: 'Limited Edition',
    };

    return badgeMap[formattedBadge] || formattedBadge;
  }

  _handleButtonClick(e) {
    e.preventDefault();
    const currentVariant = this._getCurrentVariant();
    const product = this.displayProduct || this.product;

    if (!currentVariant) {
      const selectEl = this.variantSelectRef.value;
      if (selectEl) {
        try {
          selectEl.showPicker();
        } catch (err) {
          selectEl.focus();
        }
      }

      return;
    }

    if (!currentVariant.available) {
      if (product.coming_soon_metafield) {
        const modalBackInStockEvent = new CustomEvent(`modal-back-in-stock:open`, { detail: { product: product, variant_id: currentVariant.id } });
        document.dispatchEvent(modalBackInStockEvent);
      }
      return;
    }

    this._addVariant(e, currentVariant);
  }

  _renderQuickAdd() {
    const product = this.tempProduct || this.displayProduct || this.product;

    return html`
      <div class="product-card__quick-add" @click=${this._showVariants}>
        <span class="product-card__quick-add-title">Quick Add to Bag</span>
        <span class="product-card__quick-add-mobile">+ Quick add</span>
        <div
          class="product-card__quick-add-container ${this.variantsOpen
            ? 'open'
            : ''}"
        >
          ${!this.addingToCart
            ? html`<div class="product-card__quick-add-variants">
                ${map(
                  product.variants,
                  (variant) =>
                    html`
                      <button
                        type="button"
                        class="product-card__variant-button"
                        ?disabled=${!variant.available}
                        @click=${(e) => this._addVariant(e, variant)}
                      >
                        ${variant.option1}
                      </button>
                    `
                )}
              </div>`
            : html`<div class="product-card__quick-add-loading">
                <bouncing-ellipsis></bouncing-ellipsis>
              </div>`}
        </div>
      </div>
    `;
  }

  _renderVariantSelect() {
    const product = this.displayProduct || this.product;
    const currentVariant = this._getCurrentVariant();
    this.selectedVariant = currentVariant || this.selectedVariant;
    const loggedIn = window.Shopify.customer_logged_in;

    const buttonClass = (() => {
      if (!loggedIn && product.coming_soon_metafield) {
        return 'btn-primary';
      }
      if (!!currentVariant && !currentVariant.available) {
        return 'btn-secondary';
      }
      return 'btn-primary';
    })();

    const buttonDisabled = (() => {
      if (!loggedIn && product.coming_soon_metafield) {
        return false;
      }
      return this.addingToCart ||
      (!!currentVariant && !currentVariant.available);
    })();

    return html`
      <div class="product-card__variant-select">
        <select
          class="form-select"
          ${ref(this.variantSelectRef)}
          name="variantId-${this.product?.id}"
          key="select-${product?.id}"
          id="variant-${product?.id}"
          aria-label="${window.theme.strings.product_card.select_option.replace(
            '{{ option }}',
            product.options[0] || 'Size'
          )}"
          @change=${(e) => {
            const variant = product.variants.find(
              (v) => v.id == e.target.value
            );
            this._handleVariantSelectChange(e, variant);
          }}
        >
          ${product?.variants?.length > 1
            ? html`<option
                value=""
                disabled
                ?selected=${!this.selectedVariant && !currentVariant}
              >
                ${window.theme.strings.product_card.select_option.replace(
                  '{{ option }}',
                  product.options[0] || 'Size'
                )}
              </option>`
            : nothing}
          ${map(product.variants, (variant) => {
            const isSelected = this.selectedVariant
              ? variant.id === this.selectedVariant.id
              : variant.id === currentVariant?.id;
            return html`
              <option
                value="${variant.id}"
                ?selected=${isSelected || product?.variants.length === 1}
              >
                ${variant.option1}
                ${!variant.available
                  ? ` ${product.coming_soon_metafield ? window.theme.strings.selectComingSoon : window.theme.strings.selectSoldOut}`
                  : ''}
              </option>
            `;
          })}
        </select>
        ${ (loggedIn && product.coming_soon_metafield) ? nothing : html`
          <button
            @click=${this._handleButtonClick}
            class="product-card__add-to-cart ${ buttonClass } btn w-100"
            ?disabled=${ buttonDisabled }
            data-product-id="${product.id}"
            data-variant-id="${currentVariant ? currentVariant.id : nothing}"
          >
            ${this.addingToCart
              ? html`<bouncing-ellipsis></bouncing-ellipsis>`
              : this._getButtonText(product, loggedIn)}
          </button>
        `}
      </div>
    `;
  }

  render() {
    const product = this.tempProduct || this.displayProduct || this.product;
    this.siblings = this.siblings?.filter(
      (sibling) => sibling.online_store_url
    );

    return html`
      <div class="product-card__media-container">
        ${this.wishlistFeatures ? this._renderCheckbox() : nothing}
        <div class="product-card__media">
          <div
            class="product-card__media-placeholder"
            style="padding-top: 155%"
          >
            ${this._renderMedia()} ${this._renderBadges()}
          </div>
        </div>
        ${this.mode === 'quick-add' ? html`${this._renderQuickAdd()}` : nothing}
      </div>
      <div class="product-card__info">
        <div class="product-card__info-title">
          <a
            href="/products/${product.handle}"
            title="${product.title}"
            class="product-card__info-title-link"
            ><span>${product.title}</span>
          </a>
          ${!this.hideWishlist
            ? html`<wishlist-button
                product-id=${product.id}
                product-image=${ifDefined(product?.media[0]?.src)}
                ${ref(this.wishlistButtonRef)}
                wishlist-id=${ifDefined(this.wishlistId)}
                product-title=${ifDefined(product?.title)}
                product-category=${ifDefined(product?.category)}
              ></wishlist-button>`
            : nothing}
        </div>
        <div
          class="product-card__info-wrapper"
          @mouseleave=${this._handleHoverOut}
          @blur=${this._handleHoverOut}
        >
          ${this._renderPrice(product)} ${this._renderDiscount()}
          ${this.siblings !== null &&
          !this.hideSwatches &&
          this.siblings.length > 0
            ? html`
                <fieldset class="product-card__swatches">
                  <legend class="sr-only">${product.title} swatches</legend>
                  ${map(this.siblings, (sibling) =>
                    sibling.title === this.product.title
                      ? html`${this._renderSwatch(sibling)}`
                      : nothing
                  )}
                  ${map(this.siblings, (sibling, index) =>
                    sibling.title !== this.product.title && index < 4
                      ? html`${this._renderSwatch(sibling)}`
                      : nothing
                  )}
                  ${map(this.siblings, (sibling, index) =>
                    sibling.title !== this.product.title &&
                    index > 4 &&
                    this.swatchesOpen
                      ? html`${this._renderSwatch(sibling)}`
                      : nothing
                  )}
                  ${this.siblings?.length > 5
                    ? html`<button
                        @click=${this._handleSwatches}
                        class="product-card__swatches-button"
                        type="button"
                        aria-label="Load more swatches"
                      >
                        ${this.swatchesOpen
                          ? window.theme.strings.product_card.less
                          : window.theme.strings.product_card.more}
                      </button>`
                    : nothing}
                </fieldset>
              `
            : nothing}
        </div>
      </div>
      ${this.mode === 'variant-select'
        ? html`${this._renderVariantSelect()}`
        : nothing}
    `;
  }
}

window.customElements.get('product-card') ||
  window.customElements.define('product-card', ProductCard);
